import { useContext, useEffect, useRef, useState } from 'react';
import { WelletContext } from '../../../context/WelletContext';

import { useDispatch, useSelector } from 'react-redux';
import { setCanRefresh } from '../../../actions/appActions';
import { setReservation } from '../../../actions/reservationActions';
import { useTranslation } from 'react-i18next';
import { getQueryString } from '../../../helpers/url';
import { CircleSpinner } from 'react-spinners-kit';
import {
  convertTime12to24,
  formatDateNormal,
  formatDateTime,
  getDateFromOffset,
  getLocale,
} from '../../../helpers/dates';
import { closestIndexTo, format } from 'date-fns';
import Header from '../../../components/Common/Header/Header';
import Button from '../../../components/Common/Button/Button';
import BottomSheet from '../../../components/BottomSheet/BottomSheet';
import BookSelector from '../../../components/BookSelector/BookSelector';
import { Navigation, Pagination } from 'swiper/modules';

import styles from './VenueDetail.module.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';

import './styles.css';
import { Skeleton } from '@mui/material';
import ContentApp from '../../../components/ContentApp';
import MarkerIcon from '../../../components/icons/MarkerIcon';
import QrIcon from '../../../components/icons/QrIcon';
import Line from '../../../components/Common/Line/Line';
import ProductModal from '../../../components/Common/ProductModal/ProductModal';
import ShareIcon from '../../../components/icons/ShareIcon';
import Qr from '../../../components/Common/Qr/Qr';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import { sendMessageToParent } from '../../../helpers/iframe';
import { setExtraHours } from '../../../actions/extraProductActions';
import { lockSchedule } from '../../../services/reservations/reservationsService';
import { setAvailableTime } from '../../../actions/timerActions';
import ErrorBox from '../../../components/Common/ErrorBox/ErrorBox';
import BottomSheetVar from '../../../components/BottomSheet/BottomSheetVar';

const VenueDetail = ({ history }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const welletContext = useContext(WelletContext);
  const app = useSelector((state) => state.app);
  const reservation = useSelector((state) => state.reservation);
  const locale = getLocale(app.language);
  const showId = getQueryString('showId');
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(null);
  const [timeSelected, setTimeSelected] = useState(null);
  const [today, setToday] = useState(null);
  const [openQr, setOpenQr] = useState(false);
  const [loadingIntervals, setLoadingIntervals] = useState(true);
  const [hasRestaurant, setHasRestaurant] = useState(true);
  const [externalPlatform, setExternalPlatform] = useState(null);
  const [externalPlaftormInfo, setExternalPlaftormInfo] = useState(null);
  const qrLink = useSelector((state) => state.associates.user.qrUserUrl);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [intervals, setIntervals] = useState([]);
  const swiperTimeRef = useRef(null);
  const swiperRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [lockError, setLockError] = useState(null);
  const swiperAttributesRef = useRef(null);
  const [peekOffset, setPeekOffset] = useState(0);
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: app.name,
          url: qrLink,
        });
      } else {
        console.warn('Web Share API not supported in this browser.');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const getShow = async () => {
    try {
      const result = await welletContext.apis.tickets.get(`/shows/get/`, {
        params: {
          showId: showId,
          lang: app.language,
          details: true,
          reservationType: 'RESTAURANT',
          images: true,
          videos: true,
          IsFeature: true,
        },
      });

      setShow(result.data);
      setHasRestaurant(result.data.reservationTypes.includes('RESTAURANT'));

      const todayDate = new Date(getDateFromOffset(result.data.utcOffset));

      const minutes = todayDate.getMinutes();
      todayDate.setMinutes(minutes === 0 ? minutes : minutes + (15 - (minutes % 15)), 0, 0);

      setToday(todayDate);

      if (
        !reservation.date ||
        formatDateTime(reservation.date, reservation.performance) < todayDate
      ) {
        dispatch(
          setReservation({
            ...reservation,
            date: format(todayDate, 'yyyy-MM-dd'),
          }),
        );
      }

      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };
  const getExternalPlatform = async () => {
    try {
      const result = await welletContext.apis.tickets.get(
        `/shows/${showId}/external-plaftorm/config?reservationType=RESTAURANT`,
      );
      setExternalPlaftormInfo(result.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getSchedule = async (date, reservationType, paxs = null, hour = null) => {
    setLoadingIntervals(true);
    try {
      let params = `showId=${showId}&date=${date}&reservationType=${reservationType}&details=true`;
      if (hour) {
        params += `&hour=${hour}`;
      }
      if (paxs) {
        params += `&paxs=${paxs}`;
      }

      const result = await welletContext.apis.tickets.get(`/shows/GetSchedule?${params}`);

      if (result.data.schedules.length === 0) {
        setIntervals([]);
      }

      const isExternal = result.data.scheduleFromExternalPlatform;
      setExternalPlatform(isExternal);
      const formattedTimes = result.data?.schedules?.map((time) => {
        const time24 = new Date(date + 'T' + convertTime12to24(time.timeStart));

        if (time.timeMinutesStart >= 0 && time.timeMinutesStart <= 360)
          time24.setDate(time24.getDate() + 1);

        return {
          time24,
          soldOut: time.isSoldOut,
          availabilityRefId: time.availabilityRefId,
          attributes: time.attributes,
        };
      });
      formattedTimes.sort();

      const allIntervals = formattedTimes.map(
        ({ time24, soldOut, availabilityRefId, attributes }) => ({
          time: time24,
          soldOut,
          availabilityRefId,
          attributes,
        }),
      );

      const filteredIntervals = allIntervals.filter((x) => {
        if (!externalPlatform) {
          return !x.soldOut;
        }

        return !x.soldOut && Array.isArray(x.attributes) && x.attributes.includes('default');
      });
      const attrsSet = new Set();

      filteredIntervals.forEach((interval) => {
        if (Array.isArray(interval.attributes)) {
          interval.attributes.forEach((attr) => attrsSet.add(attr));
        }
      });
      const attrsArray = Array.from(attrsSet);

      setAvailableAttributes(attrsArray);
      const attrToSelect = attrsArray.length === 1 ? attrsArray[0] : null;

      setSelectedAttribute(attrToSelect);

      setIntervals(filteredIntervals);
      // setClosedTime(
      //   referenceDate,
      //   allIntervals.map((item) => item.time),
      // );

      //setLoadingIntervals(false);
    } catch (error) {
      console.error(error);
    }
    setLoadingIntervals(false);
  };

  const setClosedTime = (date, intervals) => {
    if (swiperTimeRef.current && swiperTimeRef.current.swiper) {
      const index = closestIndexTo(date, intervals);
      swiperTimeRef.current.swiper.slideTo(index);
    }
  };
  const attributeTranslations = {
    default: t('translateAttribute.default'),
    counter: t('translateAttribute.counter'),
    highTop: t('translateAttribute.highTop'),
    outdoor: t('translateAttribute.outdoor'),
    bar: t('translateAttribute.bar'),
  };
  const headerHtml = () => {
    return (
      <div className='mt-4 pb-2'>
        <h1 className='font-large m-0'>{show.name}</h1>
        <span className='text-secondary'>{show.city.name}</span>
      </div>
    );
  };

  const onTimeClick = async (time, availabilityRefId) => {
    setSelectedTime({ time, availabilityRefId });
    setSelectedAttribute(null);
    setLockError(null);

    const selectedInterval = intervals.find((item) => format(item.time, 'HH:mm') === time);
    let shouldShowAttributeSelector = false;

    if (externalPlatform) {
      const attributes = selectedInterval?.attributes || [];
      const uniqueAttrs = [...new Set(attributes)];
      shouldShowAttributeSelector = uniqueAttrs.length > 1;
    } else {
      shouldShowAttributeSelector = selectedInterval && !selectedInterval.soldOut;
    }

    if (shouldShowAttributeSelector && peekOffset === 0) {
      setPeekOffset(70);
    } else if (!shouldShowAttributeSelector && peekOffset > 0) {
      setPeekOffset(0);
    }
    if (!externalPlatform) {
      await proceedToNextScreen(time, availabilityRefId, null);
      return;
    }
    if (!selectedInterval || !Array.isArray(selectedInterval.attributes)) {
      setAvailableAttributes([]);
      setSelectedAttribute(null);
      return;
    }

    const finalAttrs = [...new Set(selectedInterval.attributes)];
    setAvailableAttributes(finalAttrs);
    if (swiperAttributesRef.current?.swiper) {
      setTimeout(() => {
        swiperAttributesRef.current.swiper.update();
        if (finalAttrs.length > 1) {
          swiperAttributesRef.current.swiper.slideTo(0);
        }
      }, 0);
    }

    if (finalAttrs.length === 1) {
      setSelectedAttribute(finalAttrs[0]);
      await proceedToNextScreen(time, availabilityRefId, finalAttrs[0]);
    }
  };

  const proceedToNextScreen = async (time, availabilityRefId, attr) => {
    const reservationInfo = {
      ...reservation,
      showId: showId,
      showName: show.name,
      performance: time,
      availabilityRefId: availabilityRefId ?? null,
      products: null,
      imageUrl: show.imageUrl,
      attribute: attr,
    };

    dispatch(setReservation(reservationInfo));

    if (!externalPlatform) {
      history.push({
        pathname: '/reservation-check',
        search: `?show=${show.name}`,
      });
      return;
    }

    try {
      const lockResult = await lockSchedule(welletContext, {
        pax: reservation.paxs,
        reservationDateLocalTime: `${reservation.date}T${time}`,
        reservationType: 'RESTAURANT',
        showId,
        attribute: attr,
      });

      if (lockResult?.errorType === 'SLOT_NOT_AVAILABLE') {
        setLockError(t('reservation.slotNotAvailable'));
        return; 
      }

      const reservationToken = lockResult?.reservation_Token;

      if (lockResult?.expiresAt) {
        dispatch(setAvailableTime(lockResult.expiresAt));
      }

      if (reservationToken) {
        dispatch(
          setReservation({
            ...reservationInfo,
            lockToken: reservationToken,
          }),
        );
      }
      history.push({
        pathname: '/reservation-check',
        search: `?show=${show.name}&platform=${externalPlatform}${
          reservationToken ? `&reservationToken=${reservationToken}` : ''
        }`,
      });
    } catch (error) {
      console.error('Error locking schedule:', error);

      setLockError(t('reservation.errorTryAgain'));
    }
  };

  const onAttributeSelect = async (attr) => {
    if (!selectedTime) return;

    const { time, availabilityRefId } = selectedTime;
    setSelectedAttribute(attr);
    setLockError(null);

    await proceedToNextScreen(time, availabilityRefId, attr);
  };
  const onSocialClick = (social) => {
    window.open(social.value, '_blank');
  };

  const onEventClick = (eventId) => {
    history.push({
      pathname: '/event',
      search: `?eventId=${eventId}`,
    });
  };
  useEffect(() => {
    if (externalPlatform) {
      getExternalPlatform();
    }
  }, [externalPlatform]);

  const getLocation = () => {
    const address = show.address.split(',').map((str) => str.trim());
    const textCity = show.city.nameLong.split(',').map((str) => str.trim());
    let secondLine = show.postalCode;

    if (address.length > 1) secondLine = `${address[1]}, ${secondLine}`;

    return (
      <div>
        <div>{address[0]}</div>
        <div>{secondLine}</div>
        <div>{`${textCity[0]}, ${textCity[1]}`}</div>
        <div>{textCity[2]}</div>
        {show.latitude && show.longitude && (
          <div className={styles.locationMap}>
            {!mapLoaded && (
              <Skeleton
                variant='rectangular'
                sx={{
                  bgcolor: 'grey.900',
                  width: '100%',
                  height: '100%',
                  borderRadius: '.4rem',
                }}
              />
            )}

            <img
              className={`${mapLoaded ? '' : 'd-none'}`}
              src={`https://tile1.gosurmaps.com/styles/gosur/static/${show.longitude},${show.latitude},16/400x400@2x.png`}
              onLoad={() => setMapLoaded(true)}
              alt='map'
            />
            <div className={`${styles.marker} ${mapLoaded ? '' : 'd-none'}`}>
              <MarkerIcon size={32} color={'#ea4335'} />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderButtons = () => {
    if (!show.productCategories || show.productCategories.length === 0) {
      return;
    }

    return (
      <div className='mt-4 pt-4' style={{ borderTop: '1px solid #3d3d3d' }}>
        {show.productCategories.map((category) => (
          <div className='mb-3' key={category.id}>
            <Button onClick={() => handleClick(category.id)} size={'normal'} text={category.name} />
          </div>
        ))}
        {/* {(() => {
          const groupCategory = show.reservationTypes.find((category) => category === 'GROUP');
          return groupCategory ? (
            <div className='mb-3' key={groupCategory.id}>
              <Button onClick={() => handleClickGroup()} size='normal' text={'Group'} />
            </div>
          ) : null;
        })()} */}
      </div>
    );
  };

  const renderEvents = () => {
    if (show.events <= 0) return;

    return (
      <div className='mt-4 pt-4' style={{ borderTop: '1px solid #3d3d3d' }}>
        <div className='row'>
          <div className='col fw-bold'>{t('nextEvents')}</div>
          <div className='col-auto text-tertiary font-tiny my-auto'>
            <Link to={'/event-selector?showId=' + showId} className='text-secondary'>
              {t('viewAll')}
            </Link>
          </div>
        </div>

        <div className='event-list mt-3'>
          <Swiper spaceBetween={25} slidesPerView={'auto'}>
            {show.events.map((item) => (
              <SwiperSlide key={item.id}>
                <div className={styles.eventItem} onClick={() => onEventClick(item.id)}>
                  <div>
                    <img src={item.bannerImage} alt='banner-img' />
                  </div>
                  <div className='font-tiny fw-bold mt-2'>{item.name}</div>
                  <div className='font-tiny text-lighter-gray mt-2'>
                    {formatDateNormal(item.startDate, locale)}
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    );
  };

  const renderBookSelector = () => {
    return (
      <div className='mt-1 pt-3'>
        <div className='book-selector-container'>
          {today && (
            <div className='mb-3'>
              <BookSelector today={today} lang={app.language} onChange={onBookSelectorChange} />
            </div>
          )}

          {loadingIntervals ? (
            <div className='mt-3 mb-3'>
              <Swiper
                spaceBetween={10}
                slidesPerView={'auto'}
                centeredSlides={true}
                centeredSlidesBounds={true}
                ref={swiperTimeRef}
              >
                {[...Array(5)].map((_, i) => (
                  <SwiperSlide key={i}>
                    <div
                      style={{
                        backgroundColor: '#191919',
                        borderRadius: '5px',
                        height: '38px',
                        width: '78px',
                      }}
                    ></div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            <>
              <div className='mt-3 mb-3'>
                <Swiper
                  spaceBetween={10}
                  slidesPerView={'auto'}
                  centeredSlides={true}
                  centeredSlidesBounds={true}
                  ref={swiperTimeRef}
                >
                  {intervals.length === 0
                    ? [...Array(5)].map((_, i) => (
                        <SwiperSlide key={i}>
                          <button className='button-filter empty font-small'></button>
                        </SwiperSlide>
                      ))
                    : intervals.map((item, i) => (
                        <SwiperSlide key={i}>
                          <button
                            className={`button-filter font-small ${
                              selectedTime?.time === format(item.time, 'HH:mm') ? 'active' : ''
                            }`}
                            onClick={() =>
                              onTimeClick(format(item.time, 'HH:mm'), item.availabilityRefId)
                            }
                          >
                            {format(item.time, 'h:mm aaa')}
                          </button>
                        </SwiperSlide>
                      ))}
                </Swiper>
              </div>

              {selectedTime && availableAttributes.length > 1 && (
                <>
                  <div
                    className='mb-2'
                    style={{
                      borderTop: '1px solid #262835',
                    }}
                  >
                    <div className='font-1rem fw-600 mt-2'>{t('seatType')}</div>
                  </div>

                  <div>
                    <Swiper
                      ref={swiperAttributesRef}
                      spaceBetween={10}
                      slidesPerView={'auto'}
                      centeredSlides={true}
                      centeredSlidesBounds={true}
                    >
                      {availableAttributes.map((attr) => {
                        const displayText =
                          attributeTranslations[attr] ||
                          attr.charAt(0).toUpperCase() + attr.slice(1);

                        return (
                          <SwiperSlide key={attr} style={{ width: 'auto' }}>
                            <button
                              className={`button-filter font-small ${
                                selectedAttribute === attr ? 'active' : ''
                              }`}
                              style={{
                                minWidth: '90px',
                                width: 'auto',
                                padding: '0 16px',
                                whiteSpace: 'nowrap',
                              }}
                              onClick={() => onAttributeSelect(attr)}
                            >
                              {displayText}
                            </button>
                          </SwiperSlide>
                        );
                      })}
                    </Swiper>
                  </div>
                </>
              )}
              {lockError && (
                <div className=' mt-3'>
                  <ErrorBox title={t('scheduleIsNotAvailable')} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const contentHtml = () => {
    return (
      <div style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}>
        {hasRestaurant ? renderBookSelector() : renderButtons()}

        <div className={!show?.reservationTypes || !hasRestaurant ? '' : 'hide-content'}>
          {hasRestaurant && renderButtons()}
          {/* {renderEvents()} */}
          {show.description && show.description.length > 0 && (
            <div className='mt-4 pt-4' style={{ borderTop: '1px solid #3d3d3d' }}>
              <div className='fw-bold'>{t('about')}</div>
              <div
                className='text-lighter-gray mt-2 description'
                dangerouslySetInnerHTML={{ __html: show.description }}
              />
            </div>
          )}

          <div className='mt-4 pt-4' style={{ borderTop: '1px solid #3d3d3d' }}>
            <div className='fw-bold'>{t('location')}</div>
            <div className='text-lighter-gray mt-2'>{getLocation()}</div>
          </div>
          {show.socialNetworks && show.socialNetworks.length > 0 && (
            <div className='mt-4 pt-4' style={{ borderTop: '1px solid #3d3d3d' }}>
              <div className='fw-bold'>{t('follow')}</div>
              <div className='text-lighter-gray mt-2'>
                {show.socialNetworks.map((p) => (
                  <div
                    className='text-primary'
                    onClick={() => onSocialClick(p)}
                    key={p.socialNetwork}
                  >
                    {p.value}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div
            onClick={() => setOpenQr(true)}
            className='mt-3 pb-3 d-flex align-items-center justify-content-center'
          >
            <div style={{ border: '1px solid #fff', borderRadius: '50%', padding: '8px' }}>
              <QrIcon color='#fff' />
            </div>
          </div>
          {externalPlatform && externalPlaftormInfo?.showPowerBy && (
            <div className='row justify-content-center '>
              <div className={styles.poweredByContainer}>
                <span className='semi_bold mr-1 font-smaller'>Powered by&nbsp;</span>
                {externalPlaftormInfo && (
                  <img
                    className={styles.containerImgLogoPlatform}
                    src={externalPlaftormInfo?.logoUrl}
                    alt='logo-restaurant'
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleClick = (productCategoryId) => {
    if (show.purchaseTemplate === 'TRANSFER') {
      history.push({
        pathname: '/transfer-selector',
        search: `?showId=${showId}&showName=${show.name}`,
      });
    } else {
      history.push({
        pathname: '/calendar-ticket-selector',
        search: `?showId=${showId}&productCategoryId=${productCategoryId}`,
      });
    }
  };

  // const handleClickGroup = () => {
  //   history.push({
  //     pathname: '/calendar-ticket-selector-group',
  //     search: `?showId=${showId}&showName=${show.name}&reservationType=GROUP`,
  //   });
  // };

  const onBookSelectorChange = (e) => {
    getSchedule(e.date, 'RESTAURANT', e.pax, e.time);
    setSelectedTime(null);
    setSelectedAttribute(null);
    setAvailableAttributes([]);
    setLockError(null);
    if (swiperAttributesRef.current?.swiper) {
      setTimeout(() => swiperAttributesRef.current.swiper.update(), 100);
    }
  };

  useEffect(() => {
    if (show !== null && today !== null && hasRestaurant)
      getSchedule(
        reservation.date ? reservation.date : format(today, 'yyyy-MM-dd'),
        'RESTAURANT',
        reservation.paxs,
        reservation.performance ? reservation.performance : format(today, 'HH:mm'),
      );
  }, [show, today]);

  useEffect(() => {
    if (intervals) {
      const referenceDate = new Date(reservation.date + 'T' + reservation.performance);
      setClosedTime(
        referenceDate,
        intervals.map((item) => item.time),
      );
    }
  }, [intervals]);

  useEffect(() => {
    dispatch(setCanRefresh(false));
    dispatch(setExtraHours(null));
    getShow();
    return () => {
      dispatch(setCanRefresh(true));
    };
  }, []);

  return (
    <>
      {loading ? (
        <div className='loaderDiv'>
          <CircleSpinner
            className='spinner-app mx-auto'
            style={{ margin: 'auto' }}
            size={40}
            color='var(--color-font-primary)'
          />
        </div>
      ) : (
        <>
          <Header
            onClick={() =>
              app.isEmbedded
                ? sendMessageToParent(window, {
                    type: 'GO_BACK',
                  })
                : history.push('/restaurant-list')
            }
            absolute={true}
          />

          <div className={styles.pageHeader}>
            <div className={`${styles.pageSection} ${styles.header}`}>
              <div className={styles.background}>
                <Swiper
                  ref={swiperRef}
                  className={`swiper-show ${styles.swiperContainer}`}
                  pagination={true}
                  modules={[Navigation, Pagination]}
                  lazy={true}
                  style={{
                    '--swiper-pagination-top': window.innerHeight - 320 + 'px',
                    '--swiper-pagination-bottom': 'none',
                    '--swiper-pagination-color': '#fff',
                    '--swiper-pagination-bullet-inactive-color': '#999999',
                    '--swiper-pagination-bullet-inactive-opacity': '1',
                    '--swiper-pagination-bullet-size': '7px',
                    '--swiper-pagination-bullet-horizontal-gap': '6px',
                  }}
                >
                  {show?.videos &&
                    show.videos.map((video, index) => (
                      <SwiperSlide key={`video-${index}`}>
                        <div className={styles.overlay}>
                          <video
                            webkit-playsinline='true'
                            x5-playsinline='true'
                            playsInline
                            autoPlay
                            loop
                            muted
                            src={video.videoUrl}
                            alt='media'
                            style={{ width: '100%' }}
                          />
                        </div>
                      </SwiperSlide>
                    ))}

                  <SwiperSlide>
                    <div className={styles.overlay}>
                      <img src={!isMobile ? show.imageUrl : show.bannerImageUrl} alt={show.name} />
                    </div>
                  </SwiperSlide>

                  {show?.images &&
                    show.images.map((image, index) => (
                      <SwiperSlide key={`image-${index}`}>
                        <div className={styles.overlay}>
                          <img src={image.imageUrl} alt='media' />
                        </div>
                      </SwiperSlide>
                    ))}
                </Swiper>
              </div>
            </div>
          </div>
          <ContentApp classes='p-0 overflow-hidden'>
            <BottomSheetVar
              initialPosition={window.innerHeight - 100 - 290}
              header={headerHtml()}
              content={contentHtml()}
              peekOffset={peekOffset}
            />

            <ProductModal
              animationClass='fade-up-down'
              open={openQr}
              hasBackground
              onClose={() => setOpenQr(false)}
              fullScreen
              backIcon
              customStyle={{
                backgroundColor: 'transparent',
                border: 'none',
              }}
            >
              <>
                <ContentApp>
                  <div className='card align-items-center p-3'>
                    <div className={styles.imageContainer}>
                      <img src={show.logoUrl} alt='logo' />
                    </div>
                    <div className='font-large fw-semibold my-2 text-center'>{show.name}</div>
                    <Line color='#353535' className='w-100 my-4' />

                    <Qr
                      qrUrl={show.link}
                      logo={show.logoUrl}
                      welletContext={welletContext}
                      logoBorder='ROUNDED'
                    />

                    <div className='font-large text-center fw-semibold m-3'>
                      {t('scanToMakeReservation')}
                    </div>
                  </div>

                  <div
                    className='text-center text-secondary font-small mt-3'
                    onClick={() => handleShare()}
                  >
                    {t('shareLinkToBook')}
                    <ShareIcon size={25} color={'#7e7e7e'} styles={{ marginLeft: '4px' }} />
                  </div>
                </ContentApp>
              </>
            </ProductModal>
          </ContentApp>
        </>
      )}
    </>
  );
};

export default VenueDetail;
